import { useEffect, useRef } from 'react';
import Hls, { ErrorDetails, ErrorTypes, Events } from 'hls.js';
import _ from 'lodash';
import { Box } from '@mui/material';
import { socketConnection } from '../../contexts/WebSocketContext';
import { WebSocketResponse, WebSocketResponseTypes } from '../../types';
import { EventStreamStatus } from '../../constants/StreamConstants';

interface HlsVideoPlayerProps {
  src: string;
  videoRef: React.MutableRefObject<HTMLVideoElement>;
  // optional overrides
  autoPlay?: boolean;
  controls?: boolean;
  onLoadedMetadata?: () => void;
  height?: string;
  width?: string;
  eventId?: string;
  customStyles?: React.CSSProperties;
  refreshPreview?: boolean;
  resetRefreshState?: () => void;
}

const HlsVideoPlayer = ({
  src,
  autoPlay = false,
  controls = true,
  eventId,
  onLoadedMetadata = undefined,
  height = '100%',
  width = '100%',
  customStyles = {},
  refreshPreview = false,
  resetRefreshState,
  videoRef,
}: HlsVideoPlayerProps) => {
  const hlsInstance = useRef<Hls>();

  useEffect(() => {
    if (eventId) {
      socketConnection.on('message', (data) => {
        try {
          const parsedData: WebSocketResponse = data && JSON.parse(JSON.stringify(data));
          if (parsedData?.type === WebSocketResponseTypes.EVENT_STREAM_UPDATES) {
            const { status, isQueued, hasFailed } = parsedData.details.stream;
            if (!isQueued && !hasFailed && status === EventStreamStatus.STARTED) {
              console.debug(`Stream Started, triggering hls re-setup`);
              setTimeout(() => setupHLS(), 1000 * 60);
            }
          }
        } catch (error) {}
      });
      return () => {
        socketConnection.off('message');
      };
    }
  }, [eventId]);

  useEffect(() => {
    if (refreshPreview) {
      setupHLS();
      !_.isUndefined(resetRefreshState) && resetRefreshState();
    }
  }, [refreshPreview]);

  const setupHLS = () => {
    if (!Hls.isSupported()) {
      console.log('HLS not supported in Browser');
      return;
    }

    if (hlsInstance.current) {
      hlsInstance.current.destroy();
    }

    const hls = new Hls({
      fragLoadingMaxRetry: 3,
      fragLoadingRetryDelay: 2000,
      manifestLoadingMaxRetry: 3,
      manifestLoadingRetryDelay: 2000,
      appendErrorMaxRetry: 3,
      nudgeMaxRetry: 3,
    });
    hls.loadSource(src);
    hls.attachMedia(videoRef.current);

    hls.on(Events.ERROR, (event, data) => {
      if (data.fatal) {
        switch (data.type) {
          case ErrorTypes.NETWORK_ERROR:
            hls.startLoad();
            break;
          case ErrorTypes.MEDIA_ERROR:
            // added only few fragment/buffer errors that cannot be recovered
            const FragmentErrors = [
              ErrorDetails.FRAG_PARSING_ERROR,
              ErrorDetails.FRAG_DECRYPT_ERROR,
              ErrorDetails.FRAG_LOAD_ERROR,
            ];
            const BufferErrors = [
              ErrorDetails.BUFFER_APPENDING_ERROR,
              ErrorDetails.BUFFER_APPEND_ERROR,
              ErrorDetails.BUFFER_INCOMPATIBLE_CODECS_ERROR,
              ErrorDetails.BUFFER_ADD_CODEC_ERROR,
            ];
            if (FragmentErrors.includes(data.details)) {
              console.error('HLS Fragment Error Occurred - stop loading', src, event, data);
              hls.stopLoad();
            } else if (BufferErrors.includes(data.details)) {
              console.error('HLS Buffer Error Occurred - stop loading', src, event, data);
              hls.stopLoad();
            } else {
              hls.recoverMediaError();
            }
            break;
          default:
            console.error('Cannot Recover HLS Fatal Error - destroying instance', src, event, data);
            hls.destroy();
        }
      } else {
        hls.startLoad();
      }
    });

    hlsInstance.current = hls;
  };

  useEffect(() => {
    setupHLS();

    const handleLoadedMetadata = () => !_.isUndefined(onLoadedMetadata) && onLoadedMetadata();
    videoRef.current.addEventListener('loadedmetadata', handleLoadedMetadata);

    return () => {
      videoRef.current &&
        videoRef.current.removeEventListener('loadedmetadata', handleLoadedMetadata);
      hlsInstance.current && hlsInstance.current.destroy(); // this is to destroy the hls context and free up resources on unmount
    };
  }, []);

  return (
    <Box height={height} width={width}>
      <video
        ref={videoRef}
        controls={controls}
        autoPlay={autoPlay}
        {...(customStyles ? { style: customStyles } : {})}
        controlsList="fullscreen play timeline volume remoteplayback playbackrate"
        width="100%"
        height="100%"
      />
    </Box>
  );
};

export default HlsVideoPlayer;
